import { RTLHelper } from '../../Forms/components/RichTextEditorCK/helper/rtl.helper';
import { ImageHelper } from './image.helper';

export const LIST_STYLE_TYPES_ORDERED = ['decimal', 'lower-alpha', 'lower-roman'];

export class EmailHelper {

    /**
     *  Converts fontSize inline style values from px to em units, based on the fontSize passed in
     */
    static convertFontSizeUnitsToEM(content: string, baseFontSize: number = 14): string {
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(content, 'text/html');

        this.convertNodeFontSizeUnitsToEM(htmlDoc.children[0], baseFontSize);

        return new XMLSerializer().serializeToString(htmlDoc);
    }

    /**
     * Internal fontSize function, separate so it can be recursive
     * em = fontSizeInPx / baseFontSizeInPx
     */
    private static convertNodeFontSizeUnitsToEM(htmlElement, baseFontSize: number): void {
        let pxSize: number = baseFontSize;

        let emDefault: number = null;
        switch (htmlElement.tagName) {
            case 'H1':
                emDefault = 2;
                break;
            case 'H2':
                emDefault = 1.5;
                break;
            case 'H3':
                emDefault = 1.17;
                break;
        }

        if (emDefault) {
            pxSize = baseFontSize * emDefault;
        }

        if (htmlElement?.style?.fontSize?.indexOf('em') !== -1) {
            // It's already sized with em, we don't need to modify it but we do need to figure out the equivilant pxSize to pass to the children
            pxSize = baseFontSize * +htmlElement.style.fontSize.split('em')[0];
        }

        if (htmlElement?.style?.fontSize?.indexOf('px') !== -1) {
            pxSize = +htmlElement.style.fontSize.split('px')[0];
            htmlElement.style.fontSize = (pxSize / baseFontSize) + 'em';
        }

        for (let i = 0; i < htmlElement.children.length; i++) {
            this.convertNodeFontSizeUnitsToEM(htmlElement.children[i], pxSize);
        }
    }

    /**
     * Applys styling to the rich text editor content to display correctly in twig
     */
    static getTextContentStyledForEmail(text: string): string {
        if (!text) {
            return '';
        }

        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(text, 'text/html');

        this.formatRTEParagraphsForEmail(htmlDoc);
        this.formatRTEListsForEmail(htmlDoc);
        this.setCKEditorListDirectionAttributes(htmlDoc);
        this.restrictInlineImageWidth(htmlDoc);
        this.updateLineHeightForEmail(htmlDoc);
        this.updateBlockQuoteStyles(htmlDoc);

        const body = htmlDoc.querySelector('body');

        return this.adjustWhiteSpaceFonts(body.innerHTML);
    }

    /**
     * Calculates margin to add to text containing a quill indent class
     */
    private static convertQuillIndentsToInlineStyles(htmlDoc): void {
        const indentedElemenets: HTMLElement[] = htmlDoc.querySelectorAll('[class*="ql-indent"]');
        indentedElemenets.forEach(element => {
            if (element.tagName === 'LI') {
                return;
            }

            const classes: string = element.classList.value;
            if (!classes) {
                return;
            }
            const startIndex = classes.indexOf('ql-indent-') + 'ql-indent-'.length;
            const indentLevel = +classes.charAt(startIndex);
            const oneIndentLevelMargin = 3; // ems
            const margin = `${oneIndentLevelMargin * indentLevel}em`;

            element.style.marginLeft = margin;
        });
    }

    private static adjustWhiteSpaceFonts(innerHTML: string): string {
        const courierNewRegexAdjustment: RegExp = /(font-family: &quot;Courier New&quot;, Courier, monospace;)/g;
        const lucidaRegexAdjustment: RegExp = /font-family: &quot;Lucida Sans Unicode&quot;, &quot;Lucida Grande&quot;, sans-serif;/g;
        const timesRegexAdjustment: RegExp = /font-family: &quot;Times New Roman&quot;, Times, serif;/g;
        const trebuchetRegexAdjustment: RegExp = /font-family: &quot;Trebuchet MS&quot;, Helvetica, sans-serif;/g;
        const comicSansRegexAdjustment: RegExp = /font-family: &quot;Comic Sans MS&quot;, Helvetica, sans-serif;/g;

        let htmlAdjustedForWhiteSpaceFonts = innerHTML.replace(
            courierNewRegexAdjustment,
            'font-family: Courier New, Courier, monospace;'
        );
        htmlAdjustedForWhiteSpaceFonts = htmlAdjustedForWhiteSpaceFonts.replace(
            lucidaRegexAdjustment,
            'font-family: Lucida Sans Unicode, Lucida Grande, sans-serif;'
        );
        htmlAdjustedForWhiteSpaceFonts = htmlAdjustedForWhiteSpaceFonts.replace(
            timesRegexAdjustment,
            'font-family: Times New Roman, Times, serif;'
        );
        htmlAdjustedForWhiteSpaceFonts = htmlAdjustedForWhiteSpaceFonts.replace(
            trebuchetRegexAdjustment,
            'font-family: Trebuchet MS, Helvetica, sans-serif;'
        );
        htmlAdjustedForWhiteSpaceFonts = htmlAdjustedForWhiteSpaceFonts.replace(
            comicSansRegexAdjustment,
            'font-family: Comic Sans MS, Helvetica, sans-serif;'
        );

        return htmlAdjustedForWhiteSpaceFonts;
    }

    /**
     * Applies inline style resets on document
     */
    private static formatRTEParagraphsForEmail(htmlDoc: Document): void {
        const paragraphElements: NodeListOf<HTMLElement> = htmlDoc.querySelectorAll('p');

        paragraphElements.forEach(element => {
            this.applyInlineStyleResetOnElement(element);
            this.convertParagraphIndents(element);
        });
    }

    /**
     * Applies inline style resets on provided element
     */
    private static applyInlineStyleResetOnElement(element: HTMLElement) {
        element.style.letterSpacing = 'normal';
        element.style.setProperty('margin-top', '0px');
        element.style.setProperty('margin-bottom', '0px');
    }

    private static convertParagraphIndents(element: HTMLElement): void {
        for (const childNode of Array.from(element.childNodes)) {
            if (childNode.nodeType === Node.TEXT_NODE) {
                childNode.nodeValue = childNode.nodeValue.replace(/\t/g, '\xa0\xa0\xa0\xa0');
            }
        }
    }

    /**
     * Formats quill lists to be email safe
     */
    private static formatRTEListsForEmail(htmlDoc: Document): void {
        const orderedLists: HTMLElement[] = Array.from(htmlDoc.querySelectorAll('ol'));
        const unorderedLists: HTMLUListElement[] = Array.from(htmlDoc.querySelectorAll('ul'));

        for (const orderedList of orderedLists) {
            this.applyInlineStyleResetOnElement(orderedList);
            this.formatListSafeForEmail(orderedList, true);
        }

        for (const unorderedList of unorderedLists) {
            this.applyInlineStyleResetOnElement(unorderedList);
            this.formatListSafeForEmail(unorderedList, false);
        }
    }

    private static formatListSafeForEmail(list: HTMLElement, isOrderedList: boolean): void {
        if (!list.parentElement || list.parentElement.nodeName !== 'BODY' || list.children?.length === 0) {
            return;
        }

        const listItems: HTMLElement[] = <HTMLElement[]>Array.from(list.children);
        listItems.forEach(childElement => {
            if (childElement.classList.length === 0) {
                childElement.classList.add('ql-indent-0');
            }
            childElement.className = childElement.className.replace('ql-agpr-list-item', '');
            if (!isOrderedList) {
                childElement.style.listStyleType = 'disc';
            }
        });
        const orderedListRoot: HTMLElement = this.createListElement(listItems, 0, isOrderedList);

        list.innerHTML = '';
        list.classList.add('ql-agpr-list');
        Array.from(orderedListRoot.children).forEach(element => {
            list.appendChild(element);
        });
    }

    /**
     * create a list using passed in list items
     */
    private static createListElement(listItems: HTMLElement[], indentLevel: number, isOrderedList: boolean): HTMLElement {
        let currentList: HTMLElement, listStyleType: string;
        if (isOrderedList) {
            currentList = document.createElement('OL');
            listStyleType = LIST_STYLE_TYPES_ORDERED[indentLevel % 3];
        } else {
            currentList = document.createElement('UL');
            listStyleType = 'disc';
        }
        currentList.classList.add('ql-agpr-list');

        let listParentIndex: number = -1;
        let nestedListItems: HTMLElement[] = [];
        listItems.forEach((listItem, index) => {
            const indentLevelIndex = listItem.className.indexOf('ql-indent-');
            if (indentLevelIndex !== -1) {
                const listItemIndentLevel: number = +listItem.className.charAt((indentLevelIndex) + 10);
                if (listItemIndentLevel > indentLevel) {
                    // this list element will be handled in the nested list
                    nestedListItems.push(listItem);
                } else {
                    // encounter a list item on the current indent level
                    if (nestedListItems.length > 0) {
                        this.createAndAppendList(currentList, nestedListItems, indentLevel, listParentIndex);
                        nestedListItems = [];
                    }
                    listItem.classList.add('ql-agpr-list-item');
                    listItem.style.listStyleType = listStyleType;
                    currentList.appendChild(listItem);
                    listParentIndex = currentList.children.length - 1;
                }
                if (index === listItems.length - 1 && nestedListItems.length > 0) {
                    this.createAndAppendList(currentList, nestedListItems, indentLevel, listParentIndex);
                    nestedListItems = [];
                }
            } else {
                console.log('should not get here, list items coming out of quill should have ql-indent classes');
            }
        });

        return currentList;
    }

    private static createAndAppendList(currentList: HTMLElement, nestedListItems: HTMLElement[], indentLevel: number, listParentIndex: number) {
        let nestedList: HTMLElement;
        const nestedListIndentLevel = indentLevel + 1;
        const lowestListStyleType = nestedListItems.slice().sort((a, b) => Number(a.className.slice(-1)) - Number(b.className.slice(-1)))
            [0].style.listStyleType;
        if (lowestListStyleType === 'disc') {
            nestedList = this.createListElement(nestedListItems, nestedListIndentLevel, false);
        } else {
            nestedList = this.createListElement(nestedListItems, nestedListIndentLevel, true);
        }
        if (listParentIndex === -1) {
            // if there is no list item, append the list directly
            currentList.appendChild(nestedList);
        } else {
            // otherwise, append it to the list item
            currentList.children[listParentIndex].appendChild(nestedList);
        }
    }

    /**
     * Formats lists in valid email format to a format quill.js can work with
     */
    static formatEmailSafeListsForQuill(content: string): string {
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(content, 'text/html');

        const orderedLists: HTMLOListElement[] = Array.from(htmlDoc.querySelectorAll('ol'));
        const unorderedLists: HTMLUListElement[] = Array.from(htmlDoc.querySelectorAll('ul'));

        for (const orderedList of orderedLists) {
            this.formatListSafeForQuill(orderedList);
        }

        for (const unorderedList of unorderedLists) {
            this.formatListSafeForQuill(unorderedList);
        }

        const body = htmlDoc.querySelector('body');

        return body.innerHTML;
    }

    private static formatListSafeForQuill(list: HTMLElement) {
        if (list?.parentElement?.nodeName !== 'BODY') {
            return;
        }

        const listItems = Array.from(list.querySelectorAll('li'));
        list.innerHTML = '';
        listItems.forEach(listItem => {
            list.appendChild(listItem);
            // appendChiild moves listItem, leaving the empty nested list, so we need to remove the empty list
            if ((<HTMLElement>listItem.lastChild).tagName === 'OL' || (<HTMLElement>listItem.lastChild).tagName === 'UL') {
                listItem.removeChild(listItem.lastChild);
            }
        });
    }

    /**
     * Quill uses class style direction in the editor, so for email we need to convert to inline attribute
     */
    private static convertQuillDirectionClassToDirAttribute(htmlDoc: Document): void {
        const rtlElements: NodeListOf<HTMLElement> = htmlDoc.querySelectorAll('.ql-direction-rtl');
        rtlElements.forEach(rtlElement => {
            rtlElement.setAttribute('dir', 'rtl');
        });
    }

    private static setCKEditorListDirectionAttributes(htmlDoc: Document): void {
        RTLHelper.addDirectionAttribute(htmlDoc.body);
    }

    // set 100% width to inline images
    static restrictInlineImageWidth(htmlDoc: Document): void {
        const images: HTMLImageElement[] = Array.from(htmlDoc.querySelectorAll('img'));
        for (const image of images) {
            if (!image.style.maxWidth) {
                image.style.maxWidth = '100%';
            }
        }
    }

    // We need the simple version above because we want to render without user waiting on getting image widths in page builder
    // This function is more for different email clients so it could be run just before sending
    static async setInlineImageWidth(text: string, maxWidth?: number): Promise<string> {
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(text, 'text/html');

        const images: HTMLImageElement[] = Array.from(htmlDoc.querySelectorAll('img'));
        for (const image of images) {
            const indentClassName = image.parentElement.className;
            const indentLevel = indentClassName ? +indentClassName.charAt(indentClassName.indexOf('ql-indent-') + 10) : 0;
            let margin = 0;
            if (!isNaN(indentLevel)) {
                const defaultFontSize = 14, oneIndentLevelMargin = 3;
                margin = defaultFontSize * oneIndentLevelMargin * indentLevel;
            }

            let imgWidth: number = 640;
            try {
                imgWidth = await ImageHelper.getImageWidth((<HTMLImageElement>image).src);
            } catch (e) {}

            // for Outlook desktop
            if (!image.hasAttribute('width')) {
                image.setAttribute('width', Math.min((maxWidth || 640) - margin, imgWidth).toString());
            }
            if (!image.style.maxWidth) {
                image.style.maxWidth = imgWidth + 'px';
            }
        }

        return htmlDoc.querySelector('body').innerHTML;
    }

    static updateLineHeightForEmail(document: Document | string): string {
        let htmlDoc: Document;
        if (typeof document === 'string') {
            const parser = new DOMParser();
            htmlDoc = parser.parseFromString(document, 'text/html');
        } else {
            htmlDoc = document;
        }

        const parentsWithLineHeight: HTMLElement[] = Array.from(htmlDoc.querySelectorAll('[style*="line-height"]'));
        parentsWithLineHeight.forEach(parent => {
            (Array.from(parent.children) as HTMLElement[]).forEach(element => {
                if (element.style.fontSize) {
                    element.style.lineHeight = parent.style.lineHeight;
                }
            });
        });

        const elementsWithLineHeight = Array.from(htmlDoc.querySelectorAll('[style*="line-height"]'));
        (elementsWithLineHeight as HTMLElement[]).forEach(element => {
            element.style.lineHeight = +element.style.lineHeight * 100 + '%';
        });

        return htmlDoc.querySelector('body').innerHTML;
    }

    static formatLineHeightForRTE(content: string): string {
        if (content && !content.startsWith('<') && !content.endsWith('>')) {
            content = `<p>${content}</p>`;
        }
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(content, 'text/html');

        const elementsWithLineHeight = Array.from(htmlDoc.querySelectorAll('[style*="line-height"]'));
        const compatibleLineHeights = [1, 1.5, 2, 2.5, 3];
        (elementsWithLineHeight as HTMLElement[]).forEach(element => {
            let lineHeight = element.style.lineHeight;
            if (element.style.lineHeight.endsWith('%')) {
                lineHeight = '' + parseFloat(element.style.lineHeight) / 100;
            }
            const lineHeightFloat = parseFloat(lineHeight);
            if (compatibleLineHeights.indexOf(lineHeightFloat) > -1) {
                element.style.lineHeight = '' + lineHeightFloat;
            } else {
                const closestCompatibleLineHeight = compatibleLineHeights.reduce((x, y) => {
                    const absX = Math.abs(x - lineHeightFloat);
                    const absY = Math.abs(y - lineHeightFloat);

                    return absX < absY ? x : y;
                });
                element.style.lineHeight = '' + closestCompatibleLineHeight;
            }
        });

        const body = htmlDoc.querySelector('body');

        return body.innerHTML;
    }

    static updateBlockQuoteStyles(htmlDoc: Document): void {
        const blockQuotes: HTMLElement[] = Array.from(htmlDoc.querySelectorAll('blockquote'));
        for (const blockQuote of blockQuotes) {
            blockQuote.style.borderLeft = '5px solid #ccc';
            blockQuote.style.fontStyle = 'italic';
            blockQuote.style.marginLeft = '0';
            blockQuote.style.marginRight = '0';
            blockQuote.style.overflow = 'hidden';
            blockQuote.style.paddingLeft = '1.5em';
            blockQuote.style.paddingRight = '1.5em';
        }
    }
}
